if (!customElements.get('credit-products')) {
  class CreditProducts extends HTMLElement {
    constructor() {
      super();
      this._boundOnClick = null;
      this._isSubmitting = false;

      const currency = window.Shopify?.currency?.active || 'USD';
      const language = window.Shopify?.locale || 'en-US';
      this.moneyFormatter = new Intl.NumberFormat(language, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }

    connectedCallback() {
      this.creditCents = Number(this.getAttribute('data-credit-cents') || '0') || 0;

      this.cards = Array.from(this.querySelectorAll('[data-cp-card]'));
      this.summaryRowsEl = this.querySelector('[data-cp-summary-rows]');
      this.subscribeSaveRow = this.querySelector('[data-cp-subscribe-save-row]');
      this.subscribeSaveEl = this.querySelector('[data-cp-subscribe-save]');
      this.totalEl = this.querySelector('[data-cp-total]');
      this.checkoutBtn = this.querySelector('[data-cp-checkout]');
      this.bottomTotalEl = this.querySelector('[data-cp-bottom-total]');

      this._boundOnClick = (e) => this.onClick(e);
      this.addEventListener('click', this._boundOnClick);

      this.cards.forEach((card) => {
        const jsonEl = card.querySelector('[data-cp-variants-json]');
        let variants = [];
        try {
          variants = JSON.parse(jsonEl?.textContent || '[]');
        } catch {
          variants = [];
        }
        card._variants = variants;
        card._selectedVariantId = null;
      });

      this.renderSummary();
    }

    disconnectedCallback() {
      if (this._boundOnClick) this.removeEventListener('click', this._boundOnClick);
    }

    money(cents) {
      return this.moneyFormatter.format((Number(cents) || 0) / 100);
    }

    parseVariantLabel(variant) {
      if (variant?.label) return String(variant.label);
      const t = String(variant?.title || variant || '').trim();
      const m = t.match(/(\d+)/);
      const first = m ? m[1] : (t.split(/\s+/)[0] || t);
      return `${first}/MONTH`;
    }

    openCard(card) {
      card.classList.add('is-open');
    }

    closeCard(card, { reset = false } = {}) {
      card.classList.remove('is-open');
      if (reset) {
        card._selectedVariantId = null;
        const variantBtns = card.querySelectorAll('[data-cp-variant]');
        variantBtns.forEach((b) => b.classList.remove('is-active'));
        const cancelBtn = card.querySelector('[data-cp-cancel]');
        if (cancelBtn) cancelBtn.classList.remove('is-enabled');
      }
    }

    setSelectedVariant(card, variantId) {
      const id = Number(variantId);
      const variant = (card._variants || []).find((v) => Number(v.id) === id);
      if (!variant) return;

      card._selectedVariantId = id;
      const variantBtns = card.querySelectorAll('[data-cp-variant]');
      variantBtns.forEach((b) => {
        b.classList.toggle('is-active', Number(b.getAttribute('data-cp-variant')) === id);
      });

      const cancelBtn = card.querySelector('[data-cp-cancel]');
      if (cancelBtn) cancelBtn.classList.add('is-enabled');

      this.renderSummary();
    }

    resetByBlockId(blockId) {
      const card = this.cards.find((c) => c.getAttribute('data-cp-block-id') === blockId);
      if (!card) return;
      this.closeCard(card, { reset: true });
      this.renderSummary();
    }

    getSelectedItems() {
      const items = [];
      this.cards.forEach((card) => {
        if (!card._selectedVariantId) return;
        const v = (card._variants || []).find((vv) => Number(vv.id) === Number(card._selectedVariantId));
        if (!v) return;

        const displayName = card.getAttribute('data-cp-display-name') || 'Item';
        items.push({
          blockId: card.getAttribute('data-cp-block-id') || '',
          variantId: Number(v.id),
          variantTitle: String(v.title || ''),
          variantLabel: String(v.label || ''),
          price: Number(v.price || 0),
          subPrice: Number(
            v.subscription_price != null && v.subscription_price !== ''
              ? v.subscription_price
              : v.price || 0,
          ),
          sellingPlanId: v.selling_plan != null ? Number(v.selling_plan) : null,
          displayName,
        });
      });
      return items;
    }

    renderSummary() {
      const items = this.getSelectedItems();
      const compareSubtotal = items.reduce((sum, i) => sum + (Number(i.price) || 0), 0);
      const subscribeSubtotal = items.reduce((sum, i) => sum + (Number(i.subPrice) || 0), 0);
      const subscriptionDiscount = Math.max(0, compareSubtotal - subscribeSubtotal);
      const credit = items.length ? this.creditCents : 0;
      const total = Math.max(0, subscribeSubtotal - credit);

      if (this.summaryRowsEl) {
        if (!items.length) {
          this.summaryRowsEl.innerHTML = '';
        } else {
          this.summaryRowsEl.innerHTML = items.map((i) => {
            const planLabel = this.parseVariantLabel({ title: i.variantTitle, label: i.variantLabel }).toLowerCase();
            return `
              <div class="credit-products__row" data-cp-summary-row>
                <div class="credit-products__row-left">
                  <span><em>${this.escapeHtml(i.displayName)}</em> (${this.escapeHtml(planLabel)})</span>
                  <button type="button" class="credit-products__remove" data-cp-remove="${this.escapeHtml(i.blockId)}">REMOVE</button>
                </div>
                <div>${this.escapeHtml(this.money(i.price))}</div>
              </div>
            `;
          }).join('');
        }
      }

      if (this.subscribeSaveRow && this.subscribeSaveEl) {
        if (items.length && subscriptionDiscount > 0) {
          this.subscribeSaveRow.style.display = '';
          this.subscribeSaveEl.textContent = `-${this.money(subscriptionDiscount)}`;
        } else {
          this.subscribeSaveRow.style.display = 'none';
        }
      }

      const creditRow = this.querySelector('[data-cp-credit-row]');
      const creditValue = this.querySelector('[data-cp-credit]');
      if (creditRow) creditRow.style.display = items.length ? '' : 'none';
      if (creditValue) creditValue.textContent = items.length ? `-${this.money(credit)}` : this.money(0);

      if (this.totalEl) this.totalEl.textContent = this.money(total);

      if (this.bottomTotalEl) {
        const suffix = this.bottomTotalEl.getAttribute('data-cp-bottom-suffix') || '';
        const glue = suffix && !/^\s/.test(suffix) ? ' ' : '';
        this.bottomTotalEl.textContent = items.length ? `${this.money(subscribeSubtotal)}${glue}${suffix}` : '';
      }

      if (this.checkoutBtn) this.checkoutBtn.disabled = !items.length || this._isSubmitting;
    }

    escapeHtml(str) {
      return String(str || '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
    }

    async checkout() {
      const items = this.getSelectedItems();
      if (!items.length) return;
      if (this._isSubmitting) return;

      this._isSubmitting = true;
      if (this.checkoutBtn) this.checkoutBtn.disabled = true;

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: items.map((i) => {
              const line = { id: i.variantId, quantity: 1 };
              if (i.sellingPlanId) line.selling_plan = i.sellingPlanId;
              return line;
            }),
          }),
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err?.description || res.statusText);
        }

        this._isSubmitting = false;
        this.renderSummary();
      } catch (e) {
        console.error('credit-products checkout error', e);
        this._isSubmitting = false;
        this.renderSummary();
      }
    }

    onClick(e) {
      const openBtn = e.target.closest('[data-cp-open]');
      if (openBtn) {
        const card = openBtn.closest('[data-cp-card]');
        if (card) this.openCard(card);
        return;
      }

      const variantBtn = e.target.closest('[data-cp-variant]');
      if (variantBtn) {
        const card = variantBtn.closest('[data-cp-card]');
        if (!card) return;
        this.setSelectedVariant(card, variantBtn.getAttribute('data-cp-variant'));
        return;
      }

      const cancelBtn = e.target.closest('[data-cp-cancel]');
      if (cancelBtn) {
        const card = cancelBtn.closest('[data-cp-card]');
        if (card) this.closeCard(card, { reset: true });
        this.renderSummary();
        return;
      }

      const removeBtn = e.target.closest('[data-cp-remove]');
      if (removeBtn) {
        this.resetByBlockId(removeBtn.getAttribute('data-cp-remove') || '');
        return;
      }

      const checkoutBtn = e.target.closest('[data-cp-checkout]');
      if (checkoutBtn) {
        this.checkout();
      }
    }
  }

  customElements.define('credit-products', CreditProducts);
}
